import { AutoComplete, Modal, TimeInput } from "@pickit/pickit-components";
import { Field, Form, FormSpy } from "react-final-form";
import Button from "component/button/Button";
import { TemplateCreationModalPropsType } from "./types";
import { validationSchema } from "./schemas";
import "./TemplateCreationModal.scss";
import useValidationSchema from "hooks/useValidationSchema";
import FrequencyComponentContainer from "../frequencyCompoent/FrequencyComponentContainer";
import { TemplateFormValues } from "../types";
import "./i18n";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

export const TemplateCreationModal: React.FC<
  TemplateCreationModalPropsType
> = ({
  isOpen,
  regions,
  workzones,
  actualRegion,
  formRef,
  fetchingWorkzones,
  testId,
  onClose,
  setDirty,
  onSubmit,
  onUpdateWorkzones,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isOpen) {
      params.set("modal", "create-template");
    } else {
      params.delete("modal");
    }
    const paramsString = params.toString();
    const newUrl = paramsString
      ? `${window.location.pathname}?${paramsString}`
      : window.location.pathname;
    window.history.pushState({}, "", newUrl);
  }, [isOpen]);

  return (
    <Modal
      width="1190px"
      height="540px"
      isOpen={isOpen}
      onClose={() => onClose()}
      testId={testId}
    >
      <div className="creation-modal-container">
        <h2 className="creation-modal-title" data-testId={`${testId}-title`}>
          {t("templateCreation:templateCreation")}
        </h2>
        <hr className="creation-modal-separate" />
        <div className="container-template-creation-form">
          <Form
            onSubmit={(values: TemplateFormValues) => {}}
            validate={useValidationSchema(validationSchema)}
            mutators={{
              setAutoCompleteValue: ([field], state, { changeValue }) => {
                if (!field.id) {
                  changeValue(state, "workzone", () => undefined);
                  return;
                }
                changeValue(state, field.id, () => field.name);
                if (
                  state.lastFormState?.active === "region" &&
                  field.id !== actualRegion
                ) {
                  changeValue(state, "workzone", () => undefined);
                  onUpdateWorkzones(field.id);
                }
              },
            }}
          >
            {({ handleSubmit, form, invalid, values }) => {
              formRef.current = form;
              return (
                <form
                  className="form-template-creation"
                  onSubmit={handleSubmit}
                >
                  <FormSpy
                    subscription={{
                      dirty: true,
                      values: true,
                      invalid: true,
                    }}
                    onChange={(pro) => {
                      setDirty(pro.dirty);
                    }}
                  />
                  <div className="container-template-creation-form-row">
                    <div className="container-template-creation-form-col-1">
                      <Field
                        name="region"
                        onChange={form.mutators.setAutoCompleteValue}
                        label={t("templateCreation:regionLabel")}
                        placeholder={t("templateCreation:regionPlaceholder")}
                        options={regions}
                        testId={`${testId}-region`}
                        overAll
                        keyProperty="id"
                        showProperty="name"
                        isValueObject
                      >
                        {(props: any) => <AutoComplete {...props} />}
                      </Field>
                    </div>
                    <div className="container-template-creation-form-col-1">
                      <Field
                        type="text"
                        name="workzone"
                        className="Admin-Pickers-input"
                        onChange={form.mutators.setAutoCompleteValue}
                        label={t("templateCreation:workzoneLabel")}
                        placeholder={t("templateCreation:workzonePlaceholder")}
                        options={workzones}
                        disabled={workzones?.length === 0 || !values.region}
                        testId={`${testId}-workzone`}
                        overAll
                        keyProperty="id"
                        showProperty="name"
                        isValueObject
                        isLoading={fetchingWorkzones}
                      >
                        {(props: any) => <AutoComplete {...props} />}
                      </Field>
                    </div>

                    <div className="container-template-creation-form-col-1">
                      <div className="frequency">
                        <FrequencyComponentContainer
                          name="frequency"
                          testId={`${testId}-frequency`}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="container-template-creation-form-row">
                    <div className="container-template-creation-form-col-1">
                      <Field
                        type="text"
                        name="slotTime.initial"
                        label={t("templateCreation:slotStartLabel")}
                        component={TimeInput}
                        className="Admin-Pickers-input"
                        placeholder={t("templateCreation:slotStartPlaceholder")}
                        testId={`${testId}-start-time`}
                      />
                    </div>
                    <div className="container-template-creation-form-col-1">
                      <Field
                        type="text"
                        name="slotTime.end"
                        label={t("templateCreation:slotEndLabel")}
                        component={TimeInput}
                        className="Admin-Pickers-input"
                        placeholder={t("templateCreation:slotEndPlaceholder")}
                        testId={`${testId}-end-time`}
                      />
                    </div>
                  </div>

                  <div className="creation-button-container">
                    <Button
                      className="button-creation-slot"
                      type="button"
                      fill="outline"
                      onClick={() => onClose()}
                      testId={`${testId}-cancel-button`}
                    >
                      {t("global:cancel")}
                    </Button>

                    <Button
                      className="button-creation-slot"
                      type="button"
                      fill="solid"
                      disabled={invalid}
                      onClick={() => onSubmit(values)}
                      testId={`${testId}-confirm-button`}
                    >
                      {t("global:create")}
                    </Button>
                  </div>
                </form>
              );
            }}
          </Form>
        </div>
      </div>
    </Modal>
  );
};
